import { type FC } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import _ from 'lodash/fp'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormController } from '@/components/ui/form/form-controller.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import {
  type AddCreatorFormValues,
  CreatorAddFormSchema,
  defaultValues,
} from '@/dialogs/AddCreatorDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { addCreatorFx } from '@/models/creators.ts'
import { useProxyCountries } from '@/modules/proxy/hooks/useProxyCountries.ts'

const ONLYFANS_BASE_URL = 'https://onlyfans.com'

interface Props extends DialogProps {}

const extractUsernameFromUrl = (text: string): string => {
  const onlyFansUrlRegex = new RegExp(
    `^https?:\\/\\/(www\\.)?${ONLYFANS_BASE_URL.replace('https://', '')}\\/([^/?#\\s]+)(\\?.*)?$`,
  )
  const match = text.match(onlyFansUrlRegex)

  if (match && match[2]) {
    return match[2]
  }

  return text
}

export const AddCreatorDialog: FC<Props> = ({ isOpen, onClose }) => {
  const addForm = useForm<AddCreatorFormValues>({
    mode: 'onSubmit',
    defaultValues: defaultValues,
    resolver: zodResolver(CreatorAddFormSchema),
  })
  const isLoading = useUnit(addCreatorFx.pending)

  const { proxyCountries, isLoading: isProxyCountriesLoading } =
    useProxyCountries()

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = addForm

  const onSubmit = async (values: AddCreatorFormValues) => {
    const { url: username, automaticProxyCountry } = values
    const url = `${ONLYFANS_BASE_URL}/${username}`

    await addCreatorFx({ url, automaticProxyCountry })
    onClose()
  }

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    event.preventDefault()
    const pastedText = event.clipboardData.getData('text')
    const username = extractUsernameFromUrl(pastedText)

    onChange(username)
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <FormProvider {...addForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Header>
            <Dialog.Title>Add a Creator</Dialog.Title>
            <Dialog.Description className="max-w-xl">
              <div className="mb-4 mt-6 flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <Controller
                    name="url"
                    control={control}
                    render={({ field }) => (
                      <FormController>
                        <FormController.Label>
                          Creator Profile URL
                        </FormController.Label>
                        <FormController.Input>
                          <TextField
                            value={field.value}
                            onChange={field.onChange}
                            onPaste={(event) =>
                              handlePaste(event, field.onChange)
                            }
                            placeholder="username"
                            prefix={`${ONLYFANS_BASE_URL}/`}
                            showPrefixSeparator
                          />
                        </FormController.Input>
                      </FormController>
                    )}
                  />
                  <span className="text-xs font-normal text-secondary-300">
                    The URL of the creator&apos;s profile on OnlyFans must
                    include
                    <a
                      className="text-primary-500"
                      href={ONLYFANS_BASE_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {' '}
                      {ONLYFANS_BASE_URL}/username
                    </a>
                  </span>
                </div>
              </div>

              <div className="mb-4 mt-6 flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-title">
                    Proxy Country
                  </span>
                  <Controller
                    name="automaticProxyCountry"
                    control={control}
                    render={({ field }) => (
                      <Select
                        disabled={isProxyCountriesLoading}
                        value={field.value}
                        onValueChange={(value) => field.onChange(value)}
                      >
                        <SelectTrigger className="w-auto font-normal">
                          <SelectValue placeholder="Please select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {proxyCountries?.map((c) => (
                              <SelectItem
                                key={c}
                                value={c}
                              >
                                {_.startCase(c)
                                  .split(' ')
                                  .map((c) => _.capitalize(c))
                                  .join(' ')}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <span className="text-xs font-normal text-secondary-300">
                    Please choose the country for the free high quality proxy we
                    provide for your OnlyFans browser. We recommend to choose a
                    proxy that is closest to most of your team members so they
                    have a low latency.
                  </span>
                </div>
              </div>
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.ClosableFooter onClose={onClose}>
            <Button
              type="submit"
              variant="default"
              className="w-full"
              disabled={!isValid || isLoading || isProxyCountriesLoading}
            >
              Add Creator
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
